/**
 * Whether the focus-mode top bar (CycleContextBar) should show for the current route.
 * Hide when the URL names a cycle, track, or session outside the pinned (active) cycle.
 */

const CYCLES_BASE = '/prioritization/cycles';
const TRACKS_BASE = '/prioritization/tracks';
const TRACK_WORKSPACE_BASE = '/prioritization/track-workspace';
const SESSIONS_PREFIX = '/prioritization/sessions/';

/** Path segments under /cycles that are not a cycle id */
const CYCLE_PATH_RESERVED = new Set(['overview', 'new', 'list']);

/** Loose UUID check for catch-all :cycleId routes */
const UUID_LIKE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface TrackLike {
  id: string;
  cycleId?: string | null;
}

export interface SessionLike {
  id: string;
  cycleId?: string | null;
}

function cycleIdFromCatchballPath(pathname: string): string | null {
  const prefix = '/prioritization/cascade/';
  if (!pathname.startsWith(prefix)) return null;
  const sub = pathname.slice(prefix.length);
  const [a, b] = sub.split('/').filter(Boolean);
  if (!a) return null;
  if (a === 'cycle' || a === 'dashboard' || a === 'compose' || a === 'inbox') {
    return b && UUID_LIKE.test(b) ? b : null;
  }
  if (UUID_LIKE.test(a)) return a;
  return null;
}

/**
 * @param pathname — location.pathname
 * @param search — URLSearchParams from location
 * @param pinnedCycleId — active focus cycle (null = caller skips bar entirely)
 * @param tracksById — for /tracks/:trackId resolution
 * @param sessionById — for /sessions/:sessionId resolution
 */
export function shouldShowFocusModeBarForRoute(
  pathname: string,
  search: URLSearchParams,
  pinnedCycleId: string,
  tracksById: Map<string, TrackLike>,
  sessionById: Map<string, SessionLike>
): boolean {
  const qCycle = search.get('cycleId');
  if (qCycle && qCycle !== pinnedCycleId) return false;

  // ── /prioritization/cycles and /prioritization/cycles/:id ───────────
  if (pathname === CYCLES_BASE || pathname.startsWith(`${CYCLES_BASE}/`)) {
    const rest = pathname.replace(new RegExp(`^${CYCLES_BASE}/?`), '');
    const segment = rest.split('/')[0] ?? '';
    if (!segment) {
      return false;
    }
    if (!CYCLE_PATH_RESERVED.has(segment) && segment !== pinnedCycleId) {
      return false;
    }
  }

  const trackIdFromBase = (base: string): string | null => {
    if (pathname === base) return null;
    if (!pathname.startsWith(`${base}/`)) return null;
    const rest = pathname.slice(base.length + 1);
    const seg = rest.split('/')[0] ?? '';
    return seg && seg !== 'list' ? seg : null;
  };

  // ── /prioritization/tracks/:trackId & track-workspace/:trackId ─────────
  const trackId = trackIdFromBase(TRACKS_BASE) ?? trackIdFromBase(TRACK_WORKSPACE_BASE);
  if (trackId) {
    const tr = tracksById.get(trackId);
    if (!tr) return true;
    const c = tr.cycleId ?? null;
    if (!c || c !== pinnedCycleId) return false;
  }

  // ── /prioritization/sessions/:id ───────────────────────────────────────
  if (pathname.startsWith(SESSIONS_PREFIX)) {
    const rest = pathname.slice(SESSIONS_PREFIX.length);
    const sessionSeg = rest.split('/')[0] ?? '';
    const staticSessionPaths = new Set(['list', 'new', '']);
    if (sessionSeg && !staticSessionPaths.has(sessionSeg)) {
      const sid = sessionSeg.split('/')[0];
      const sess = sessionById.get(sid);
      if (!sess) return true;
      const c = sess.cycleId ?? null;
      if (!c || c !== pinnedCycleId) return false;
    }
  }

  const cbCycle = cycleIdFromCatchballPath(pathname);
  if (cbCycle && cbCycle !== pinnedCycleId) return false;

  return true;
}

/**
 * What a page means for focus (owner ruling 2026-11-03): a page about one
 * cycle, track or session focuses it, and the page listing all of them clears
 * that level. Every other page leaves focus as it is, so moving between a
 * cycle's own pages keeps its focus.
 */
export type FocusTarget =
  | { kind: 'cycle' | 'track' | 'session'; id: string }
  | { kind: 'clear'; level: 'cycle' | 'track' | 'session' }
  | null;

const SESSION_PATH_RESERVED = new Set(['new', 'explore', 'list']);

export function focusTargetForPath(pathname: string): FocusTarget {
  const path = pathname.replace(/\/+$/, '');
  const instance = (base: string, reserved: ReadonlySet<string>) => {
    if (path === base) return 'list' as const;
    if (!path.startsWith(`${base}/`)) return null;
    const seg = path.slice(base.length + 1).split('/')[0] ?? '';
    if (reserved.has(seg)) return seg === 'new' || seg === 'list' ? ('list' as const) : null;
    return UUID_LIKE.test(seg) ? seg : null;
  };
  const cycle = instance(CYCLES_BASE, CYCLE_PATH_RESERVED);
  if (cycle === 'list') return { kind: 'clear', level: 'cycle' };
  if (cycle) return { kind: 'cycle', id: cycle };
  const track = instance(TRACKS_BASE, new Set(['list']));
  if (track === 'list') return { kind: 'clear', level: 'track' };
  if (track) return { kind: 'track', id: track };
  const session = instance('/prioritization/sessions', SESSION_PATH_RESERVED);
  if (session === 'list') return { kind: 'clear', level: 'session' };
  if (session) return { kind: 'session', id: session };
  return null;
}
